import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, EMPTY } from "rxjs";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { CachedProjectService } from "../../../../../shared/services/cached-project.service";
import { Job } from "../../../../../models/job";
import { FileMeta, toFileMeta } from "../../job-type-config";

interface JobTemplate {
  bucket: string;
  prefix: string;
  xwi_prefix: string;
  runfiles: string[];
  all_files: string[];
}

/**
 * Lets the user source a velocity (Vp) volume from any job in the project:
 * search/select a job, pick one of its Vp/CP volumes, and see the full path
 * of the volume that will be copied. Emits the resolved FileMeta.
 */
@Component({
  selector: "app-job-volume-picker",
  templateUrl: "./job-volume-picker.component.html",
  styleUrl: "./job-volume-picker.component.less",
  imports: [CommonModule, FormsModule, NzSelectModule, NzIconModule],
})
export class JobVolumePickerComponent {
  private cachedProjectService = inject(CachedProjectService);
  private nzNotificationService = inject(NzNotificationService);

  /** Jobs available to source a volume from. */
  readonly jobs = input<Job[]>([]);
  /** Job to default the source selection to (typically the template job). */
  readonly defaultJob = input<Job | null>(null);
  /** Regex used to filter the source job's files down to Vp volumes. */
  readonly fileRegex = input<string | undefined>(undefined);
  /** Regex used to auto-select a default volume (e.g. StartVp) within a job. */
  readonly defaultSelectionRegex = input<string | undefined>(undefined);

  /** Resolved volume that will be copied as the starting velocity. */
  readonly selection = model<FileMeta | null>(null);

  protected selectedJob = signal<Job | null>(null);

  protected compareById = (a: Job | null, b: Job | null) => a?.id === b?.id;
  protected compareByPath = (a: FileMeta | null, b: FileMeta | null) =>
    a?.path === b?.path;

  protected jobFilesResource = rxResource<JobTemplate, Job>({
    params: computed(() => this.selectedJob() ?? undefined),
    stream: ({ params }) =>
      this.cachedProjectService.getJobTemplate(params.id).pipe(
        catchError(() => {
          this.nzNotificationService.error(
            "Error",
            "Failed to fetch volumes for the selected job"
          );
          return EMPTY;
        })
      ),
  });

  /** The source job's files filtered down to Vp volumes. */
  protected vpFiles = computed<FileMeta[]>(() => {
    const all = (this.jobFilesResource.value()?.all_files ?? []).map(toFileMeta);
    const regex = this.fileRegex();
    if (!regex) return all;
    const re = new RegExp(regex, "i");
    return all.filter((f) => re.test(f.path));
  });

  constructor() {
    // Default the source job to the template job whenever it changes.
    effect(() => {
      const dj = this.defaultJob();
      untracked(() => {
        this.selectedJob.set(dj);
        this.selection.set(null);
      });
    });

    // Once a job's volumes load, auto-select the default Vp if the current
    // selection is no longer valid for this job.
    effect(() => {
      const files = this.vpFiles();
      if (this.jobFilesResource.isLoading()) return;
      untracked(() => {
        const current = this.selection();
        if (current && files.some((f) => f.path === current.path)) return;
        const defaultRegex = this.defaultSelectionRegex();
        const next = defaultRegex
          ? files.find((f) => new RegExp(defaultRegex, "i").test(f.name)) ?? null
          : null;
        this.selection.set(next);
      });
    });
  }

  protected onJobChange(job: Job | null): void {
    this.selectedJob.set(job);
    this.selection.set(null);
  }

  protected onFileChange(file: FileMeta | null): void {
    this.selection.set(file ?? null);
  }
}
